// Assets
import FontFamily from 'assets/font-family.json';

import { FC, Fragment } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
// Types
import QuizTypes from 'types/QuizTypes';
// Components
import { Input, Checkbox, Select } from 'components/Controls';
import { Button, IconButton } from 'components/Buttons';
import Accordion from 'components/Accordion';
import { ColorPicker } from 'components/ColorPicker';
import AnswerOption from './Answer';
// Icons
import { MdDragIndicator } from 'react-icons/md';
// Utilities
import { isRequired } from 'utilities/validation';
// framer-motion
import { Reorder } from 'framer-motion/dist/framer-motion';

type Props = {
  // Agent Style editor only cares about visual style, not gameplay settings
  // — renders just the font + correct/incorrect answer color controls and
  // hides randomize/highlight-checkbox/limit/delay/ranking-poll-answers.
  styleOnly?: boolean;
}

const QuestionsSettings:FC<Props> = ({ styleOnly = false }) => {
  const { t } = useTranslation();

  const { control, watch, formState: { errors }, setValue } = useFormContext();

  const { fields, append, remove } = useFieldArray<any>({
    control,
    name: `questionsSettings.answersOptions`
  });

  const watchedItems = useWatch({
    control,
    name: `questionsSettings.answersOptions`
  });

  const reorder = (newItems:any) => {
    setValue('questionsSettings.answersOptions', newItems);
  }
  
  const handleAdd = () => {
    append({ title: '', ref: uuidv4() });
    setTimeout(() => {
      const lastAnswerInput = document.getElementById(`answer-option-${fields.length}.title`);
      lastAnswerInput?.focus();
    }, 10);
  }

  const handleDelete = (index:number) => {
    remove(index);
  }

  const watchedQuestionsFont = watch('questionsSettings.font');

  const questionDelayOptions = new Array(10).fill(null).map((_, i) => ({ label: (i + 1).toString(), value: i + 1 }));

  // Read the type off the live form rather than the Redux `quiz` slice —
  // during the agent's create flow `quiz` stays null until the quiz is
  // actually saved, but the form's `type` is already set (handleProceed
  // syncs it before this dialog can even open).
  const quizType = watch('type');

  const isRankingPollQuiz = !styleOnly && quizType === QuizTypes.RankingPoll;

  const showRandomizeOrder = !styleOnly && [QuizTypes.BeatTheExpert, QuizTypes.Poll, QuizTypes.Prediction, QuizTypes.Scoring, QuizTypes.Streak].includes(quizType);
  const showLimitInput = !styleOnly && [QuizTypes.BeatTheExpert, QuizTypes.Poll, QuizTypes.Prediction, QuizTypes.Scoring, QuizTypes.Streak].includes(quizType);
  // Only these types have a "correct answer" concept at all — colors stay
  // gated on that in every mode, styleOnly included.
  const supportsAnswerColors = [QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quizType);
  const showHighlightCorrectAnswer = !styleOnly && supportsAnswerColors;
  const showAnswerColors = supportsAnswerColors;

  return (
    <div react-scroll="questionsSettings">
      <Accordion label={t('pages.quizForm.questionsSettings')} icon="settings">
        <div className="w-full max-w-2xl">
          {showRandomizeOrder ? (
            <Controller
              control={control} name="questionsSettings.randomizeOrder"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  label={t('pages.quizForm.randomizeOrderLabel')} id="randomizeQuestionOrder" name="randomizeQuestionOrder"
                  checked={value}
                  onChange={onChange}
                  helperText={t('pages.quizForm.randomizeOrderHint')}
                />
              )}
            />
          ) : null}

          {showHighlightCorrectAnswer && (
            <Controller
              control={control} name="questionsSettings.highlightCorrectAnswer"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  label={t('pages.quizForm.highlightCorrectAnswerLabel')} id="highlightCorrectAnswer" name="highlightCorrectAnswer"
                  checked={value}
                  onChange={onChange}
                  helperText={t('pages.quizForm.highlightCorrectAnswerHint')}
                />
              )}
            />
          )}

          {showLimitInput ? (
            <Fragment>
              <Controller
                control={control} name="questionsSettings.limit"
                render={({ field: { onChange, value } }) => (
                  <Input
                    label={t('pages.quizForm.questionLimitLabel')} id="questionLimit" type="number" name="questionLimit"
                    value={value || ''}
                    onChange={onChange}
                  />
                )}
              />
              <span className="block text-gray-400 text-sm font-light leading-5 mb-4">{t('pages.quizForm.questionLimitHint')}</span>
            </Fragment>
          ) : null}

          {!styleOnly && (
            <Fragment>
              <Controller
                control={control} name="questionsSettings.delay"
                render={({ field }) => (
                  <Select
                    {...field}
                    label={t('pages.quizForm.questionDelayLabel')} id="questionDelay" name="questionDelay"
                    options={questionDelayOptions}
                  />
                )}
              />
              <span className="block text-gray-400 text-sm font-light leading-5 mb-4">{t('pages.quizForm.questionDelayHint')}</span>
            </Fragment>
          )}

          <Controller
            control={control} name="questionsSettings.font"
            render={({ field }) => (
              <Select
                {...field}
                label={t('labels.fonts')} id="questionsFont" name="questionsFont"
                helperText={t('pages.quizForm.questionsFontHint')}
                required={true}
                options={FontFamily.families}
              />
            )}
          />
          <p className="text-white text-xl mb-4" style={{ fontFamily: `"${watchedQuestionsFont}", sans-serif` }}>
            {t('labels.howTextWillLook')}
          </p>

          {showAnswerColors ? (
            <div className="flex flex-col items-center gap-4 md:flex-row">
              <div className="mb-4">
                <Controller
                  control={control} name="questionsSettings.correctAnswerColor" rules={{ required: isRequired }}
                  render={({ field: { value, onChange } }) => (
                    <ColorPicker
                      label={t('pages.quizForm.correctAnswerColor')}
                      value={value}
                      required={true}
                      error={Boolean(errors.questionsSettings?.correctAnswerColor)}
                      errorText={errors.questionsSettings?.correctAnswerColor ? errors.questionsSettings.correctAnswerColor.message : ''}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
              <div className="mb-4">
                <Controller
                  control={control} name="questionsSettings.incorrectAnswerColor" rules={{ required: isRequired }}
                  render={({ field: { value, onChange } }) => (
                    <ColorPicker
                      label={t('pages.quizForm.incorrectAnswerColor')}
                      value={value}
                      required={true}
                      error={Boolean(errors.questionsSettings?.incorrectAnswerColor)}
                      errorText={errors.questionsSettings?.incorrectAnswerColor ? errors.questionsSettings.incorrectAnswerColor.message : ''}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
            </div>
          ) : null}

          {isRankingPollQuiz ? (
            <Fragment>
              <span className="divider"></span>
              <h2 className="text-white text-xl font-bold leading-6 my-4">{t('labels.answers')}</h2>
              <Reorder.Group axis="y" values={watchedItems} as="div" onReorder={reorder} className="flex flex-col gap-2 mb-4">
                {watchedItems.map((item:any, idx:number) => (
                  <Reorder.Item
                    key={`answer-option-${item.ref}`} as="div"
                    className="flex items-center gap-4"
                    value={item}
                  >
                    <IconButton onClick={() => {}}>
                      <MdDragIndicator />
                    </IconButton>
                    <AnswerOption
                      index={idx}
                      onDelete={handleDelete}
                    />
                  </Reorder.Item>
                ))}
              </Reorder.Group>
              <div className="text-right">
                <Button
                  variant="contained"
                  onClick={handleAdd}
                >{t('buttons.addAnswer')}</Button>
              </div>
            </Fragment>
          ) : null}
        </div>
      </Accordion>
    </div>
  )
}

export default QuestionsSettings;